import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import TextInput from "../ui/TextInput";
import Dropdown from "../common/Dropdown";
import SimpleFormGroup from "../common/SimpleFormGroup";
import StudioDateInput from "../ui/StudioDateInput";
import { getCompanyFileCategories } from "../../api/companyFileCategories";
import { CompanyFileCategory } from "../../types/requestsOutputs";
import {
    getCompanyFileTags,
    getCompanyUsageTypeMetadatas,
    getCompanyRightsTermMetadatas,
    getCompanyTerritoryMetadatas,
    getCompanyAllowedMediaMetadatas,
    CompanyFileTag,
    CompanyUsageTypeMetadata,
    CompanyRightsTermMetadata,
    CompanyTerritoryMetadata,
    CompanyAllowedMediaMetadata,
} from "../../api/companyMetadata";
import { LANGUAGES } from "../../utils/languages";
import { useLanguage } from "../../context/useLanguage";

export interface DatabaseFilters {
    name?: string;
    title?: string;
    description?: string;
    author?: string;
    type?: string;
    language?: string;
    categoryId?: string;
    tagId?: string;
    rightsOwner?: string;
    creditLine?: string;
    usageTypeId?: string;
    rightsTermId?: string;
    territoryId?: string;
    allowedMediaId?: string;
    createdAtFrom?: string;
    createdAtTo?: string;
    dateFrom?: string;
    dateTo?: string;
}

export interface DatabaseSort {
    sortBy: string;
    sortOrder: "asc" | "desc";
}

interface DatabaseFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentFilters: DatabaseFilters;
    onApply: (filters: DatabaseFilters) => void;
}

const DatabaseFilterModal: React.FC<DatabaseFilterModalProps> = ({ isOpen, onClose, currentFilters, onApply }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const modalContentRef = useRef<HTMLDivElement>(null);

    const [filters, setFilters] = useState<DatabaseFilters>(currentFilters);
    const [categories, setCategories] = useState<CompanyFileCategory[]>([]);
    const [tags, setTags] = useState<CompanyFileTag[]>([]);
    const [usageTypes, setUsageTypes] = useState<CompanyUsageTypeMetadata[]>([]);
    const [rightsTerms, setRightsTerms] = useState<CompanyRightsTermMetadata[]>([]);
    const [territories, setTerritories] = useState<CompanyTerritoryMetadata[]>([]);
    const [allowedMedia, setAllowedMedia] = useState<CompanyAllowedMediaMetadata[]>([]);
    const [loading] = useState(false);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            // Portaled menus / date picker must not close the modal
            if (
                target.closest(".rw-select__menu") ||
                target.closest(".rw-select__menu-portal") ||
                target.closest(".react-select__menu") ||
                target.closest(".react-select__menu-portal") ||
                target.closest(".MuiPickersPopper-root") ||
                target.closest(".rw-studio-date-popper")
            ) {
                return;
            }

            if (modalContentRef.current && !modalContentRef.current.contains(target)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            fetchAllMetadata();
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    useEffect(() => {
        setFilters(currentFilters);
    }, [currentFilters, isOpen]);

    const fetchAllMetadata = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            const [categoriesData, tagsData, usageTypesData, rightsTermsData, territoriesData, allowedMediaData] =
                await Promise.all([
                    getCompanyFileCategories({ token }),
                    getCompanyFileTags({ token }),
                    getCompanyUsageTypeMetadatas({ token }),
                    getCompanyRightsTermMetadatas({ token }),
                    getCompanyTerritoryMetadatas({ token }),
                    getCompanyAllowedMediaMetadatas({ token }),
                ]);

            setCategories(categoriesData);
            setTags(tagsData);
            setUsageTypes(usageTypesData);
            setRightsTerms(rightsTermsData);
            setTerritories(territoriesData);
            setAllowedMedia(allowedMediaData);
        } catch (error) {
            console.error("Error fetching metadata:", error);
        }
    };

    const handleFilterChange = (field: keyof DatabaseFilters, value: string) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value || undefined,
        }));
    };

    const handleApply = () => {
        // Clean up empty filters
        const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== "") {
                acc[key as keyof DatabaseFilters] = value;
            }
            return acc;
        }, {} as DatabaseFilters);

        onApply(cleanedFilters);
        onClose();
    };

    const handleClearAll = () => {
        setFilters({});
    };

    const languageOptions = LANGUAGES.map((lang) => ({
        value: lang.code,
        label: language === "FR" ? `${lang.labelFr} (${lang.nativeLabel})` : `${lang.label} (${lang.nativeLabel})`,
    }));

    const categoryOptions = categories.map((cat) => ({
        value: cat.id,
        label: cat.name,
    }));

    const tagOptions = tags.map((tag) => ({
        value: tag.id,
        label: tag.name,
    }));

    const usageTypeOptions = usageTypes.map((type) => ({
        value: type.id,
        label: type.name,
    }));

    const rightsTermOptions = rightsTerms.map((term) => ({
        value: term.id,
        label: term.name,
    }));

    const territoryOptions = territories.map((territory) => ({
        value: territory.id,
        label: territory.name,
    }));

    const allowedMediaOptions = allowedMedia.map((media) => ({
        value: media.id,
        label: media.name,
    }));

    const fileTypeOptions = [
        { value: "text", label: t("text") || "Text" },
        { value: "image", label: t("image") || "Image" },
        { value: "audio", label: t("audio") || "Audio" },
        { value: "video", label: t("video") || "Video" },
    ];

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="database-modal-overlay">
            <div className="database-modal-content file-metadata-modal" ref={modalContentRef}>
                <div className="database-modal-header">
                    <h2>{t("filterAndSort") || "Filter and Sort"}</h2>
                    <button className="database-modal-close-button" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="database-modal-body">
                    {/* Filter Section */}
                    <div className="filter-section">
                        {/* Text Filters */}
                        <SimpleFormGroup title={t("name") || "Name"} size="small">
                            <TextInput
                                value={filters.name || ""}
                                onChange={(e) => handleFilterChange("name", e.target.value)}
                                placeholder={t("filterByName") || "Filter by name"}
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={t("title") || "Title"} size="small">
                            <TextInput
                                value={filters.title || ""}
                                onChange={(e) => handleFilterChange("title", e.target.value)}
                                placeholder={t("filterByTitle") || "Filter by title"}
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={t("description") || "Description"} size="small">
                            <TextInput
                                value={filters.description || ""}
                                onChange={(e) => handleFilterChange("description", e.target.value)}
                                placeholder={t("filterByDescription") || "Filter by description"}
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={t("author") || "Author"} size="small">
                            <TextInput
                                value={filters.author || ""}
                                onChange={(e) => handleFilterChange("author", e.target.value)}
                                placeholder={t("filterByAuthor") || "Filter by author"}
                            />
                        </SimpleFormGroup>

                        {/* Dropdown Filters */}
                        <SimpleFormGroup title={t("fileType") || "File Type"} size="small">
                            <Dropdown
                                label=""
                                options={fileTypeOptions}
                                value={filters.type || ""}
                                onChange={(value) => handleFilterChange("type", value)}
                                placeholder={t("selectFileType") || "Select file type"}
                                error={false}
                                isWithoutMargin
                                loading={false}
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={t("language") || "Language"} size="small">
                            <Dropdown
                                label=""
                                options={languageOptions}
                                value={filters.language || ""}
                                onChange={(value) => handleFilterChange("language", value)}
                                placeholder={t("selectLanguage") || "Select language"}
                                error={false}
                                isWithoutMargin
                                loading={false}
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={`${t("tag")} 1`} size="small">
                            <Dropdown
                                label=""
                                options={categoryOptions}
                                value={filters.categoryId || ""}
                                onChange={(value) => handleFilterChange("categoryId", value)}
                                placeholder={`${t("select")} ${t("tag")} 1`}
                                error={false}
                                loading={loading}
                                isWithoutMargin
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={`${t("tag")} 2`} size="small">
                            <Dropdown
                                label=""
                                options={tagOptions}
                                value={filters.tagId || ""}
                                onChange={(value) => handleFilterChange("tagId", value)}
                                placeholder={`${t("select")} ${t("tag")} 2`}
                                error={false}
                                loading={loading}
                                isWithoutMargin
                            />
                        </SimpleFormGroup>

                        {/* Rights Owner and Credit Line */}
                        <SimpleFormGroup title={t("rightsOwner") || "Rights Owner"} size="small">
                            <TextInput
                                value={filters.rightsOwner || ""}
                                onChange={(e) => handleFilterChange("rightsOwner", e.target.value)}
                                placeholder={t("filterByRightsOwner") || "Filter by rights owner"}
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={t("creditLine") || "Credit Line"} size="small">
                            <TextInput
                                value={filters.creditLine || ""}
                                onChange={(e) => handleFilterChange("creditLine", e.target.value)}
                                placeholder={t("filterByCreditLine") || "Filter by credit line"}
                            />
                        </SimpleFormGroup>

                        {/* New Metadata Dropdowns */}
                        <SimpleFormGroup title={t("metadataUsageType") || "Usage Type"} size="small">
                            <Dropdown
                                label=""
                                options={usageTypeOptions}
                                value={filters.usageTypeId || ""}
                                onChange={(value) => handleFilterChange("usageTypeId", value)}
                                placeholder={t("selectUsageType") || "Select usage type"}
                                error={false}
                                loading={loading}
                                isWithoutMargin
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={t("metadataRightsTerm") || "Rights Term"} size="small">
                            <Dropdown
                                label=""
                                options={rightsTermOptions}
                                value={filters.rightsTermId || ""}
                                onChange={(value) => handleFilterChange("rightsTermId", value)}
                                placeholder={t("selectRightsTerm") || "Select rights term"}
                                error={false}
                                loading={loading}
                                isWithoutMargin
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={t("metadataTerritory") || "Territory"} size="small">
                            <Dropdown
                                label=""
                                options={territoryOptions}
                                value={filters.territoryId || ""}
                                onChange={(value) => handleFilterChange("territoryId", value)}
                                placeholder={t("selectTerritory") || "Select territory"}
                                error={false}
                                loading={loading}
                                isWithoutMargin
                            />
                        </SimpleFormGroup>

                        <SimpleFormGroup title={t("metadataAllowedMedia") || "Allowed Media"} size="small">
                            <Dropdown
                                label=""
                                options={allowedMediaOptions}
                                value={filters.allowedMediaId || ""}
                                onChange={(value) => handleFilterChange("allowedMediaId", value)}
                                placeholder={t("selectAllowedMediaSingle") || "Select allowed media"}
                                error={false}
                                loading={loading}
                                isWithoutMargin
                            />
                        </SimpleFormGroup>

                        {/* Date Filters */}
                        <div className="filter-row">
                            <SimpleFormGroup title={t("createdFrom") || "Created From"} size="small">
                                <StudioDateInput
                                    value={filters.createdAtFrom || ""}
                                    onChange={(isoDate) => handleFilterChange("createdAtFrom", isoDate)}
                                    placeholder={t("selectDate") || "Select date"}
                                />
                            </SimpleFormGroup>
                            <SimpleFormGroup title={t("createdTo") || "Created To"} size="small">
                                <StudioDateInput
                                    value={filters.createdAtTo || ""}
                                    onChange={(isoDate) => handleFilterChange("createdAtTo", isoDate)}
                                    placeholder={t("selectDate") || "Select date"}
                                />
                            </SimpleFormGroup>
                        </div>

                        <div className="filter-row">
                            <SimpleFormGroup title={t("dateFrom") || "Date From"} size="small">
                                <StudioDateInput
                                    value={filters.dateFrom || ""}
                                    onChange={(isoDate) => handleFilterChange("dateFrom", isoDate)}
                                    placeholder={t("selectDate") || "Select date"}
                                />
                            </SimpleFormGroup>
                            <SimpleFormGroup title={t("dateTo") || "Date To"} size="small">
                                <StudioDateInput
                                    value={filters.dateTo || ""}
                                    onChange={(isoDate) => handleFilterChange("dateTo", isoDate)}
                                    placeholder={t("selectDate") || "Select date"}
                                />
                            </SimpleFormGroup>
                        </div>
                    </div>
                </div>

                <div className="database-modal-footer">
                    <Button onClick={handleClearAll} label={t("clearAll") || "Clear All"} variant="primary" />
                    <div className="database-modal-footer-right">
                        <Button onClick={onClose} label={t("cancel") || "Cancel"} variant="cancel" />
                        <Button onClick={handleApply} label={t("apply") || "Apply"} variant="primary" />
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default DatabaseFilterModal;
